import { useLazyQuery } from '@apollo/client';
import { useDealPromptFeatureFlaggedFilters } from '@openx/components-deal-prompt/lib/hooks/useDealPromptFeatureFlaggedFilters';
import { buildSearchQueryInput } from '@openx/components-deal-prompt/lib/utils/buildSearchQueryInput';
import type {
  DealPromptSearchFetchArgs,
  UseDealPromptDomainSearchFetch,
} from '@openx/components-deal-prompt/lib/utils/context/type';
import { mapCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers';
import { type DealPromptFilters, dealPromptContextTypes } from '@openx/components-deal-prompt/lib/utils/types';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { useCallback } from 'react';
import { InventoryDomainSearchDocument } from '../../../graphql/query/dealPrompt/GetInventoryDomainSearch.generated';

export const useDealPromptDomainSearchFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptDomainSearchFetch => {
  const [fetchInventoryDomainSearch] = useLazyQuery(InventoryDomainSearchDocument, { fetchPolicy: 'no-cache' });
  const featureFlaggedFilters = useDealPromptFeatureFlaggedFilters();

  const fetchDealPromptDomains = useCallback(
    async ({ query, queries }: DealPromptSearchFetchArgs, abortSignal?: AbortSignal) => {
      const inputBody = {
        query: buildSearchQueryInput({ query, queries }),
        sortBy: 'totalImpressions',
        view: 'DESC',
        excludeQuery: [],
        filters: mapCriteriaToFiltersBody(criteria, dealPromptContextTypes.DOMAINS, featureFlaggedFilters),
      };

      const result = await fetchInventoryDomainSearch({
        variables: {
          input: inputBody,
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });
      return {
        cumulativeData: result?.data?.inventoryDomainSearch.body.cumulativeData ?? [],
        numberOfResults: result?.data?.inventoryDomainSearch?.body.totalRecords ?? 0,
        _error: result?.error,
      };
    },
    [fetchInventoryDomainSearch, criteria, featureFlaggedFilters]
  );

  return {
    fetchDealPromptDomains,
  };
};
